import { api } from "./client";
import type {
  ArchiveScope,
  DocumentFolderDto,
  DocumentFolderTreeDto,
  FolderAccessDto,
  FolderDetailDto,
  FolderSearchQuery,
  FolderSearchResultDto,
  BreadcrumbSegmentDto,
  FolderTemplateDto,
  BackfillResultDto,
  FolderNameResyncResultDto,
  SetFolderAccessRequest,
  WorkingBucketItemDto,
} from "@/types/api";

export interface CreateFolderInput {
  name: string;
  parentFolderId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
}

export const foldersApi = {
  roots: () => api.get<DocumentFolderDto[]>("/folders/roots"),
  // Prompt 6 — canonical server-side folder search. Powers the DMS
  // category browser and is the seam the staging filing picker will
  // retrofit to (today it loads a single entity tree via `entityTree`).
  searchFolders: (query: FolderSearchQuery = {}) =>
    api.get<FolderSearchResultDto>("/folders/search", {
      category: query.category ?? undefined,
      q: query.q ?? undefined,
      sort: query.sort ?? undefined,
      dir: query.dir ?? undefined,
      page: query.page ?? undefined,
      pageSize: query.pageSize ?? undefined,
      includeArchived: query.includeArchived ? true : undefined,
    }),
  detail: (id: string, archiveScope?: ArchiveScope) =>
    api.get<FolderDetailDto>(`/folders/${id}`, archiveScope ? { archiveScope } : undefined),
  tree: (id: string, includeArchived = false) =>
    api.get<DocumentFolderTreeDto>(
      `/folders/${id}/tree`,
      includeArchived ? { includeArchived: true } : undefined,
    ),
  breadcrumb: (id: string) =>
    api.get<BreadcrumbSegmentDto[]>(`/folders/${id}/breadcrumb`),
  entityTree: (
    entityType: string,
    entityId: string,
    options: { includeArchived?: boolean; orFallback?: boolean } = {},
  ) => {
    const params: Record<string, true> = {};
    if (options.includeArchived) params.includeArchived = true;
    if (options.orFallback) params.orFallback = true;
    return api.get<DocumentFolderTreeDto>(
      `/folders/entity/${entityType}/${entityId}`,
      Object.keys(params).length > 0 ? params : undefined,
    );
  },
  // Bugfix Bundle B — auto-provision on first detail-page render. Returns
  // the existing tree if one exists, otherwise materialises it from the
  // registered template. 404 only when no template is registered for the
  // entity type.
  ensureEntityTree: (entityType: string, entityId: string, displayName?: string | null) =>
    api.post<DocumentFolderTreeDto>(
      `/folders/entity/${entityType}/${entityId}/ensure`,
      { displayName: displayName ?? null },
    ),
  create: (input: CreateFolderInput) =>
    api.post<DocumentFolderDto>("/folders", input),
  rename: (id: string, name: string) =>
    api.put<DocumentFolderDto>(`/folders/${id}`, { name }),
  move: (id: string, newParentFolderId: string | null) =>
    api.post<DocumentFolderDto>(`/folders/${id}/move`, { newParentFolderId }),
  delete: (id: string, cascade = false) =>
    api.delete<void>(`/folders/${id}?cascade=${cascade}`),
  // Folder Rename & Archive v1.0 — soft, reversible. Distinct from delete.
  archive: (id: string, reason?: string | null) =>
    api.post<DocumentFolderDto>(`/folders/${id}/archive`, reason ? { reason } : {}),
  unarchive: (id: string) =>
    api.post<DocumentFolderDto>(`/folders/${id}/unarchive`, {}),
  // Folder Visibility & Access Control v1.0 — ADMIN-only. getAccess returns
  // the current mode + granted user ids; setAccess replaces it atomically.
  getAccess: (id: string) =>
    api.get<FolderAccessDto>(`/folders/${id}/access`),
  setAccess: (id: string, body: SetFolderAccessRequest) =>
    api.put<FolderAccessDto>(`/folders/${id}/access`, body),
};

export const folderTemplatesApi = {
  listAll: () => api.get<FolderTemplateDto[]>("/folder-templates"),
  listByEntity: (entityType: string) =>
    api.get<FolderTemplateDto[]>(`/folder-templates/${entityType}`),
  create: (input: {
    entityType: string;
    folderName: string;
    parentTemplateFolderName?: string | null;
    sortOrder: number;
  }) => api.post<FolderTemplateDto>("/folder-templates", input),
  update: (
    id: string,
    input: {
      folderName: string;
      parentTemplateFolderName?: string | null;
      sortOrder: number;
      isActive: boolean;
    },
  ) => api.put<FolderTemplateDto>(`/folder-templates/${id}`, input),
  delete: (id: string) => api.delete<void>(`/folder-templates/${id}`),
  backfill: (entityType: string) =>
    api.post<BackfillResultDto>(`/folder-templates/${entityType}/backfill`),
  // Entity Folder Naming Sweep — renames every existing EntityRoot folder of
  // a type to the entity's current display name.
  resyncNames: (entityType: string) =>
    api.post<FolderNameResyncResultDto>(`/folder-templates/${entityType}/resync-names`),
};

export const bucketApi = {
  add: (documentIds: string[]) =>
    api.post<WorkingBucketItemDto[]>("/bucket/add", { documentIds }),
  listByIds: (ids: string[]) => {
    const params = ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&");
    return api.get<WorkingBucketItemDto[]>(`/bucket${params ? "?" + params : ""}`);
  },
  importFolder: (folderId: string, recursive = false) =>
    api.post<WorkingBucketItemDto[]>(
      `/bucket/import-folder/${folderId}?recursive=${recursive}`,
    ),
};
